// client/src/api/clients.js
const API_BASE_URL = '/api'; // Your backend API base URL

/**
 * Helper to get headers with optional authentication token.
 * @param {string} token - Optional authentication token.
 * @returns {object} Headers object.
 */
const getHeaders = () => {
  const headers = {
    'Content-Type': 'application/json'
  };
  return headers;
};

/**
 * Fetches all clients for the authenticated user.
 * @returns {Promise<Array>} A promise that resolves to an array of client objects.
 */
export const fetchClients = async () => {
  const response = await fetch(`${API_BASE_URL}/clients`, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch clients');
  }
  return response.json(); // Assuming backend returns { clients: [...] }
};

/**
 * Fetches a single client by ID.
 * @param {number} clientId - The ID of the client to fetch.
 * @returns {Promise<object>} A promise that resolves to the client object.
 */
export const fetchClientById = async (clientId) => {
  const response = await fetch(`${API_BASE_URL}/clients/${clientId}`, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch client details');
  }
  return response.json(); // Assuming backend returns { client: ... }
};

/**
 * Creates a new client.
 * @param {object} clientData - Object containing client name, email, phone, address.
 * @returns {Promise<object>} A promise that resolves to the new client's info.
 */
export const createClient = async (clientData) => {
  const response = await fetch(`${API_BASE_URL}/clients`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(clientData),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create client');
  }
  return response.json();
};

/**
 * Updates an existing client.
 * @param {number} clientId - The ID of the client to update.
 * @param {object} clientData - Object containing updated client name, email, phone, address.
 * @returns {Promise<object>} A promise that resolves to a success message.
 */
export const updateClient = async (clientId, clientData) => {
  const response = await fetch(`${API_BASE_URL}/clients/${clientId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(clientData),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update client');
  }
  return response.json();
};

/**
 * Deletes a client.
 * @param {string} token - Authentication token (optional, credentials are handled via cookies).
 * @param {number} clientId - The ID of the client to delete.
 * @returns {Promise<object>} A promise that resolves to a success message.
 */
export const deleteClient = async (token, clientId) => {
  const response = await fetch(`${API_BASE_URL}/clients/${clientId}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to delete client');
  }
  return response.json();
};
